import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  ChevronUp,
  ChevronDown,
  Info,
  Plus,
  Trash2,
  User,
  Mail,
  Phone,
  Wand2,
} from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import {
  formFieldsService,
  FormFieldInput,
  ContactInfoSettings,
} from "@/services/formFieldsService";

import * as SwitchPrimitives from "@radix-ui/react-switch";
import { cn } from "@/lib/utils";
import { InfoBanner } from "@/components/ui/info-banner";
import FormFieldModal from "../modals/FormFieldModal";
import { FormFieldValues } from "../modals/FormFieldForm";

/* ------------------------------------------------------------------ */
/* ----------------------------- types ------------------------------ */
/* ------------------------------------------------------------------ */

type Agent = Tables<"agents">;
type DbFormField = Tables<"form_fields">;

interface FormField extends DbFormField {
  fieldType?: string;
}

interface SystemField {
  id: string;
  title: string;
  fieldType: string;
  icon: React.ReactNode;
  is_required: boolean;
  isSystem: true;
  toggleable?: boolean;
  enabled?: boolean;
}

type FieldSection = "in_conversation" | "post_conversation" | "pre-session";

interface AgentFormFieldsTabProps {
  agent: Agent;
  fieldSection?: FieldSection;
  onFieldsChange?: (fields: FormField[]) => void;
  onContactInfoChange?: (contactInfo: any) => void;
  isVoiceAgent?: boolean;
}

/* ------------------------------------------------------------------ */
/* --------------------------- components --------------------------- */
/* ------------------------------------------------------------------ */

interface FieldToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const FieldToggle: React.FC<FieldToggleProps> = ({
  enabled,
  onToggle,
  disabled = false,
  className,
}) => {
  // Check if this is the smaller version
  const isSmall = className?.includes("h-5 w-10");

  return (
    <SwitchPrimitives.Root
      checked={enabled}
      onCheckedChange={onToggle}
      disabled={disabled}
      className={cn(
        isSmall
          ? "relative inline-flex h-5 w-10 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors"
          : "relative inline-flex h-7 w-14 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        enabled ? "bg-[rgb(236,178,45)]" : "bg-gray-300",
        className,
      )}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          isSmall
            ? "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg ring-0 transition-transform"
            : "pointer-events-none block h-6 w-6 rounded-full bg-white shadow-lg ring-0 transition-transform",
          isSmall
            ? enabled
              ? "translate-x-5"
              : "translate-x-0"
            : enabled
              ? "translate-x-7"
              : "translate-x-0",
        )}
      />
    </SwitchPrimitives.Root>
  );
};

interface ContactInfoToggleProps {
  label: string;
  icon: React.ReactNode | null;
  enabled: boolean;
  onToggle: (checked: boolean) => void;
}

const ContactInfoToggle: React.FC<ContactInfoToggleProps> = ({
  label,
  icon,
  enabled,
  onToggle,
}) => (
  <div className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
    <div className="space-y-1 flex-1">
      <div className="flex items-center gap-2">
        {icon}
        <h3 className="font-medium text-gray-900 text-sm">{label}</h3>
      </div>
    </div>
    <div className="flex items-center space-x-2">
      <FieldToggle enabled={enabled} onToggle={onToggle} className="h-5 w-10" />
    </div>
  </div>
);

interface FieldCardProps {
  field: FormField | SystemField;
  index?: number;
  isSystemField?: boolean;
  onEdit?: (index: number) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  onDelete?: (index: number) => void;
  totalFields?: number;
}

const FieldCard: React.FC<FieldCardProps> = ({
  field,
  index,
  isSystemField = false,
  onEdit,
  onMoveUp,
  onMoveDown,
  onDelete,
  totalFields = 0,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    // Only trigger edit if the click is on the card itself, not on buttons
    if (
      e.target === e.currentTarget ||
      (e.target as Element).closest(".field-content")
    ) {
      if (!isSystemField && typeof index === "number" && onEdit) {
        onEdit(index);
      }
    }
  };

  const handleMoveUp = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMoveUp && typeof index === "number") {
      onMoveUp(index);
    }
  };

  const handleMoveDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMoveDown && typeof index === "number") {
      onMoveDown(index);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete && typeof index === "number") {
      onDelete(index);
    }
  };

  return (
    <div
      key={field.id}
      className={`flex items-center justify-between p-4 border rounded-lg bg-white ${
        isSystemField ? "bg-gray-50" : "hover:shadow cursor-pointer shadow-sm"
      } transition-all duration-200`}
      onClick={handleCardClick}
    >
      <div className="space-y-1 flex-1 field-content">
        <div className="flex items-center gap-2">
          {isSystemField && "icon" in field && field.icon}
          <h3 className="font-medium text-gray-900 text-sm">{field.title}</h3>
          {"isDefault" in field && field.isDefault && (
            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
              Default
            </span>
          )}
        </div>
        <div className="flex gap-3">
          {!isSystemField && (
            <span className="text-sm text-muted-foreground flex items-center">
              <span className="inline-block w-2 h-2 rounded-full bg-[#060520] mr-1.5" />
              {("fieldType" in field && field.fieldType) || "Text"}
            </span>
          )}
          {!isSystemField && "instructions" in field && field.instructions && (
            <span className="text-sm text-muted-foreground flex items-center">
              <Info className="h-3 w-3 mr-1 text-gray-400" />
              Has instructions
            </span>
          )}
        </div>
        {"description" in field && typeof field.description === "string" && (
          <p className="text-sm text-gray-500">{field.description}</p>
        )}
      </div>
      {!isSystemField &&
        typeof index === "number" &&
        onMoveUp &&
        onMoveDown &&
        onDelete && (
          <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMoveUp}
              disabled={index === 0}
              className="text-gray-500 hover:text-gray-700"
              type="button"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMoveDown}
              disabled={index === totalFields - 1}
              className="text-gray-500 hover:text-gray-700"
              type="button"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              className="bg-red-50 hover:bg-red-100 border-red-200 text-red-600 hover:text-red-700 transition-colors"
              type="button"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
    </div>
  );
};

interface EmptyStateBlockProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  buttonText: string;
  buttonColor: string;
  onAdd: () => void;
}

const EmptyStateBlock: React.FC<EmptyStateBlockProps> = ({
  icon,
  title,
  description,
  buttonText,
  buttonColor,
  onAdd,
}) => (
  <div className="py-10 text-center text-muted-foreground w-full border-2 border-dashed rounded-lg bg-gray-50">
    <div className="flex flex-col items-center justify-center space-y-3">
      <div className="bg-white p-3 rounded-full border shadow-sm">{icon}</div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="text-sm max-w-md mx-auto">{description}</p>
      <Button
        onClick={onAdd}
        className={`mt-2 gap-2 px-6 py-2 ${buttonColor} text-white rounded-lg font-medium transition-colors`}
      >
        <Plus className="h-4 w-4" />
        {buttonText}
      </Button>
    </div>
  </div>
);

interface AddFieldButtonProps {
  buttonText: string;
  buttonColor: string;
  onAdd: () => void;
}

const AddFieldButton: React.FC<AddFieldButtonProps> = ({
  buttonText,
  buttonColor,
  onAdd,
}) => (
  <div className="mt-4 flex justify-start">
    <Button
      onClick={onAdd}
      className={`gap-2 px-6 py-2 ${buttonColor} text-white rounded-lg font-medium transition-colors`}
    >
      <Plus className="h-4 w-4" />
      {buttonText}
    </Button>
  </div>
);

/* ------------------------------------------------------------------ */
/* --------------------------- main hook ---------------------------- */
/* ------------------------------------------------------------------ */

const AgentFormFieldsTab = ({
  agent,
  fieldSection = "in_conversation",
  onFieldsChange,
  onContactInfoChange,
  isVoiceAgent = false,
}: AgentFormFieldsTabProps) => {
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requirePhoneNumber, setRequirePhoneNumber] = useState(false);
  const [requireName, setRequireName] = useState(true);
  const [requireEmail, setRequireEmail] = useState(true);
  const { toast } = useToast();

  // Helper to update fields and notify parent
  const updateFormFields = (newFields: FormField[]) => {
    setFormFields(newFields);
    if (onFieldsChange) {
      // Filter fields by current section before passing to preview
      const sectionFields = newFields.filter(
        (field) => field.field_type === fieldSection,
      );
      onFieldsChange(sectionFields);
    }
  };

  /* ------------------------ data fetching ------------------------ */

  useEffect(() => {
    if (!agent?.id) return;
    const fetchFields = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await formFieldsService.getFormFields(
          agent.id,
          isVoiceAgent,
        );
        if (error) throw error;

        const formatted: FormField[] = (data || []).map((f, idx) => ({
          ...f,
          fieldType: (f as any).answer_type ?? "text",
          options: (f as any).options ?? undefined,
          position: f.position ?? idx,
          field_type: (f as any).field_type ?? "in_conversation",
        }));
        updateFormFields(formatted);
      } catch (err) {
        console.error(err);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load form fields.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchFields();
  }, [agent?.id, toast]);

  /* ------------------------ contact info ------------------------ */

  useEffect(() => {
    const ci =
      (agent?.contact_info as
        | {
            requirePhoneNumber?: boolean;
            requireName?: boolean;
            requireEmail?: boolean;
          }
        | undefined) ?? {};
    setRequirePhoneNumber(ci.requirePhoneNumber === true);
    setRequireName(ci.requireName !== false);
    setRequireEmail(ci.requireEmail !== false);
  }, [agent]);

  const toggleFieldRequirement = async (key: string, checked: boolean) => {
    try {
      const current = (agent.contact_info as ContactInfoSettings) ?? {};
      const updated = { ...current, [key]: checked };

      const { error } = await formFieldsService.updateContactInfoSettings(
        agent.id,
        updated,
      );
      if (error) throw error;

      if (key === "requirePhoneNumber") setRequirePhoneNumber(checked);
      if (key === "requireName") setRequireName(checked);
      if (key === "requireEmail") setRequireEmail(checked);

      // Notify preview of contact info changes
      if (onContactInfoChange && fieldSection === "pre-session") {
        onContactInfoChange(updated);
      }
      // Also notify of form fields changes
      if (onFieldsChange && fieldSection === "pre-session") {
        const sectionFields = formFields.filter(
          (field) => field.field_type === fieldSection,
        );
        onFieldsChange(sectionFields);
      }

      toast({
        title: `${key.replace("require", "")} ${
          checked ? "enabled" : "disabled"
        }`,
        description: checked
          ? `Users will now be asked for their ${key
              .replace("require", "")
              .toLowerCase()} before chat starts.`
          : `Users will no longer be asked for their ${key
              .replace("require", "")
              .toLowerCase()}.`,
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update ${key.replace("require", "")}.`,
      });
    }
  };

  /* ---------------------- convenience helpers ---------------------- */

  const handleAddField = () => {
    setEditingField(null);
    setIsModalOpen(true);
  };

  const handleEditField = (idx: number) => {
    const fld = formFields[idx];
    setEditingField(fld);
    setIsModalOpen(true);
  };

  const handleDeleteField = async (idx: number) => {
    const f = formFields[idx];
    if (!f.id) {
      const tmp = [...formFields];
      tmp.splice(idx, 1);
      updateFormFields(tmp);
      return;
    }
    try {
      const { error } = await formFieldsService.deleteFormField(f.id);
      if (error) throw error;

      const tmp = [...formFields];
      tmp.splice(idx, 1);
      const reindexed = tmp.map((ft, i) => ({ ...ft, position: i }));
      updateFormFields(reindexed);

      // Update positions for remaining fields
      const positionUpdates = reindexed
        .filter((r) => r.id)
        .map((r) => ({ id: r.id!, position: r.position }));

      if (positionUpdates.length > 0) {
        await formFieldsService.updateFieldPositions(positionUpdates);
      }

      toast({ title: "Field deleted", description: "Deleted successfully." });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete field.",
      });
    }
  };

  const moveField = async (idx: number, dir: "up" | "down") => {
    if (
      (dir === "up" && idx === 0) ||
      (dir === "down" && idx === formFields.length - 1)
    )
      return;
    const newIdx = dir === "up" ? idx - 1 : idx + 1;
    const updated = [...formFields];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updated[idx].position = idx;
    updated[newIdx].position = newIdx;
    updateFormFields(updated);

    try {
      const f1 = updated[idx];
      const f2 = updated[newIdx];

      const positionUpdates = [];
      if (f1.id) positionUpdates.push({ id: f1.id, position: idx });
      if (f2.id) positionUpdates.push({ id: f2.id, position: newIdx });

      if (positionUpdates.length > 0) {
        await formFieldsService.updateFieldPositions(positionUpdates);
      }

      toast({ title: "Field moved", description: `Moved ${dir}.` });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to move ${dir}.`,
      });
    }
  };

  const upsertFormField = async (
    values: FormFieldValues,
    id?: string | null,
  ) => {
    const opts =
      values.fieldType === "select" && values.options
        ? values.options.split("\n").filter((o) => o.trim() !== "")
        : undefined;

    const payload: FormFieldInput = {
      title: values.title,
      instructions: values.instructions || undefined,
      is_required: values.is_required || false,
      position:
        values.position ??
        formFields.reduce((max, f) => Math.max(max, f.position ?? 0), -1) + 1,
      field_type: values.field_type,
      answer_type: values.fieldType,
      options: opts ?? null,
      agent_id: agent.id,
    } as FormFieldInput & { isVoiceAgent: boolean };

    if (id) {
      return await formFieldsService.updateFormField(id, payload);
    }
    // Add isVoiceAgent flag for creation
    return await formFieldsService.createFormField({
      ...payload,
      isVoiceAgent,
    });
  };

  const handleModalSubmit = async (
    data: FormFieldValues,
    id?: string | null,
  ) => {
    try {
      const res = await upsertFormField(data, id);
      if (res.error) throw res.error;

      const returnedField = res.data!;
      const opts =
        data.fieldType === "select" && data.options
          ? data.options.split("\n").filter((o) => o.trim() !== "")
          : undefined;

      const updated = [...formFields];

      if (editingField) {
        // Update existing field
        const index = updated.findIndex((f) => f.id === editingField.id);
        if (index !== -1) {
          updated[index] = {
            ...returnedField,
            fieldType: data.fieldType,
            options: opts,
          };
        }
      } else {
        // Add new field
        updated.push({
          ...returnedField,
          fieldType: data.fieldType,
          options: opts,
        });
      }

      updateFormFields(updated);
      toast({ title: "Saved", description: "Field saved successfully." });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to save field.",
      });
    }
  };

  /* -------------------------- default field -------------------------- */

  interface DefaultField extends SystemField {
    isDefault?: boolean;
    description?: string;
  }

  // Default system fields that are automatically extracted
  const systemAnalyticsFields: DefaultField[] = [
    {
      id: "default-user-type",
      title: "User Type",
      fieldType: "text",
      icon: <User className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Lead, client, or unknown",
    },
    {
      id: "default-intent",
      title: "Intent",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Primary goal or purpose",
    },
    {
      id: "default-priority",
      title: "Priority",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Low, medium, high, or urgent",
    },
    {
      id: "default-summary",
      title: "Conversation Summary",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Key conversation content",
    },
    {
      id: "default-sentiment",
      title: "Sentiment",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Positive, neutral, or negative",
    },
    {
      id: "default-next-steps",
      title: "Next Steps",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Suggested follow-up actions",
    },
    {
      id: "default-tags",
      title: "Tags/Topics",
      fieldType: "text",
      icon: <Wand2 className="h-4 w-4 text-purple-500" />,
      is_required: true,
      isSystem: true,
      description: "Relevant business categories",
    },
  ];

  /* ----------------------- filtered list ----------------------- */

  const filteredFields = fieldSection
    ? formFields.filter((f) => f.field_type === fieldSection)
    : formFields;

  /* ------------------------------------------------------------------ */
  /* ------------------------------ render ----------------------------- */
  /* ------------------------------------------------------------------ */

  return (
    <div className="w-full">
      {isLoading ? (
        <div className="py-10 text-center text-muted-foreground">
          Loading fields...
        </div>
      ) : (
        <div className="space-y-6">
          {/* Info banner (conditional based on section) */}
          {fieldSection !== "pre-session" && (
            <InfoBanner>
              {fieldSection === "post_conversation"
                ? "Your agent automatically extracts standard conversation analytics (shown below). Add custom business-specific fields that your agent can extract from conversations without explicitly asking users for this information."
                : "Fields can be collected by the AI during a conversation with a user. The AI will ask for these fields to gather information."}
            </InfoBanner>
          )}

          {/* PRE-CHAT TAB -------------------------------------------------- */}
          {fieldSection === "pre-session" && (
            <div>
              {/* contact info toggles */}
              <div className="mb-8">
                <h3 className="text-md font-medium text-gray-700 mb-3">
                  Contact information
                </h3>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <ContactInfoToggle
                    label="Name"
                    icon={null}
                    enabled={requireName}
                    onToggle={(c) => toggleFieldRequirement("requireName", c)}
                  />
                  <ContactInfoToggle
                    label="Email"
                    icon={null}
                    enabled={requireEmail}
                    onToggle={(c) => toggleFieldRequirement("requireEmail", c)}
                  />
                  <ContactInfoToggle
                    label="Phone n°"
                    icon={null}
                    enabled={requirePhoneNumber}
                    onToggle={(c) =>
                      toggleFieldRequirement("requirePhoneNumber", c)
                    }
                  />
                </div>
              </div>

              {/* extra pre-chat fields */}
              <h3 className="text-md font-medium text-gray-700 mb-3">
                Additional pre-chat fields
              </h3>
              {filteredFields.length ? (
                <div className="grid gap-3 mt-4">
                  {filteredFields.map((f, i) => (
                    <FieldCard
                      key={f.id}
                      field={f}
                      index={i}
                      onEdit={handleEditField}
                      onMoveUp={(idx) => moveField(idx, "up")}
                      onMoveDown={(idx) => moveField(idx, "down")}
                      onDelete={handleDeleteField}
                      totalFields={filteredFields.length}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateBlock
                  icon={<Plus className="h-8 w-8 text-[#060520]" />}
                  title="No pre-chat fields"
                  description="Add fields for the AI to collect from users before the conversation starts."
                  buttonText="Add Pre-Chat Field"
                  buttonColor="bg-[#060520] hover:bg-[#0c0c37]"
                  onAdd={handleAddField}
                />
              )}
              {filteredFields.length > 0 && (
                <AddFieldButton
                  buttonText="Add Pre-Chat Field"
                  buttonColor="bg-[#060520] hover:bg-[#0c0c37]"
                  onAdd={handleAddField}
                />
              )}
            </div>
          )}

          {/* IN-CONVERSATION TAB ------------------------------------------ */}
          {fieldSection === "in_conversation" && (
            <div>
              {filteredFields.length ? (
                <div className="grid gap-3">
                  {filteredFields.map((f, i) => (
                    <FieldCard
                      key={f.id}
                      field={f}
                      index={i}
                      onEdit={handleEditField}
                      onMoveUp={(idx) => moveField(idx, "up")}
                      onMoveDown={(idx) => moveField(idx, "down")}
                      onDelete={handleDeleteField}
                      totalFields={filteredFields.length}
                    />
                  ))}
                </div>
              ) : (
                <EmptyStateBlock
                  icon={<Plus className="h-8 w-8 text-[#060520]" />}
                  title="No fields to ask during conversation"
                  description="Add fields for the AI to collect from users during conversations."
                  buttonText="Add Field"
                  buttonColor="bg-[#060520] hover:bg-[#0c0c37]"
                  onAdd={handleAddField}
                />
              )}
              {filteredFields.length > 0 && (
                <AddFieldButton
                  buttonText="Add New Field"
                  buttonColor="bg-[#060520] hover:bg-[#0c0c37]"
                  onAdd={handleAddField}
                />
              )}
            </div>
          )}

          {/* POST-CONVERSATION TAB --------------------------------------- */}
          {fieldSection === "post_conversation" && (
            <div>
              {/* System Analytics Fields */}
              <div className="mb-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    System Analytics Fields
                  </h3>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {systemAnalyticsFields.map((field) => (
                    <FieldCard key={field.id} field={field} isSystemField />
                  ))}
                </div>
              </div>

              {/* Custom Extraction Fields */}
              <div>
                <div className="mb-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Custom Extraction Fields
                  </h3>
                </div>
                {filteredFields.length ? (
                  <div className="grid gap-3">
                    {filteredFields.map((f, i) => (
                      <FieldCard
                        key={f.id}
                        field={f}
                        index={i}
                        onEdit={handleEditField}
                        onMoveUp={(idx) => moveField(idx, "up")}
                        onMoveDown={(idx) => moveField(idx, "down")}
                        onDelete={handleDeleteField}
                        totalFields={filteredFields.length}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyStateBlock
                    icon={<Wand2 className="h-8 w-8 text-purple-600" />}
                    title="No custom extraction fields defined"
                    description="Define additional fields to extract specific business information from conversations."
                    buttonText="Add Custom Extraction Field"
                    buttonColor="bg-purple-600 hover:bg-purple-700"
                    onAdd={handleAddField}
                  />
                )}
                {filteredFields.length > 0 && (
                  <AddFieldButton
                    buttonText="Add New Custom Extraction Field"
                    buttonColor="bg-purple-600 hover:bg-purple-700"
                    onAdd={handleAddField}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal for adding/editing fields */}
      <FormFieldModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingField(null);
        }}
        agent={agent}
        fieldSection={fieldSection}
        editingField={editingField}
        onSubmit={handleModalSubmit}
      />
    </div>
  );
};

export default AgentFormFieldsTab;
